import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { differenceInYears, format, isValid, parseISO } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import { dateLocale } from 'i18n';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import { useMasterChartSetup } from 'components/dashboard/healthRecords/masterChartSetupContext';

const DemographicsPage = () => {
  const { t } = useTranslation('masterchart');
  const { user } = useContext(AuthContext);
  const setup = useMasterChartSetup();

  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // Translation keys rather than text, so a language switch re-renders them.
  const [successMsg, setSuccessMsg] = useState(null);

  const { control, getValues, reset, watch } = useForm({
    defaultValues: {
      dateOfBirth: null,
      sex: '',
      weight: '',
    },
  });

  const watchedDob = watch('dateOfBirth');
  const age =
    watchedDob instanceof Date && !Number.isNaN(watchedDob.getTime())
      ? differenceInYears(new Date(), watchedDob)
      : null;

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      reset({
        // parseISO, not new Date(): the latter reads a bare "1990-04-02" as UTC
        // midnight, which is the day before west of Greenwich.
        dateOfBirth: hr.dateOfBirth ? parseISO(hr.dateOfBirth) : null,
        sex: hr.sex || '',
        weight: hr.weight != null ? String(hr.weight) : '',
      });
    } catch {
      // Blank fields after a failed load look like an empty record, and saving
      // them could overwrite the real one, so the form stays hidden until a retry.
      setFullHealthRecords(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, reset]);

  useEffect(() => {
    load();
  }, [load]);

  /** Resolves true once saved, so setup's Continue only moves on after a good write. */
  const handleSave = useCallback(async () => {
    if (!user?.uid || saving || fullHealthRecords == null) return false;
    setSaving(true);
    setError(null);
    try {
      const values = getValues();
      const weightNum = values.weight !== '' ? parseFloat(values.weight) : null;
      // format, not toISOString().split("T")[0]: that converts to UTC first, so a
      // date picked at local midnight in Nairobi was being filed as the day
      // before. A birthday is a calendar day, not an instant.
      const dob =
        values.dateOfBirth instanceof Date && isValid(values.dateOfBirth)
          ? format(values.dateOfBirth, 'yyyy-MM-dd')
          : null;

      const updates = {};
      if (dob) updates.dateOfBirth = dob;
      if (values.sex) updates.sex = values.sex;
      if (weightNum != null && !Number.isNaN(weightNum)) {
        updates.weight = weightNum;
        updates.weightDate = format(new Date(), 'yyyy-MM-dd');
      }

      const merged = {
        ...(fullHealthRecords ?? {}),
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
      setSuccessMsg('common.saved');
      return true;
    } catch {
      setError('common.failedSave');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user?.uid, saving, getValues, fullHealthRecords]);

  useEffect(() => {
    setup?.onContinue(handleSave);
  }, [setup, handleSave]);

  if (loading) {
    return (
      <div className="inner">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    // en-GB masks the field day-first (Spanish already does). The adapter default is MM/DD/YYYY, and a
    // Kenyan patient entering 2 April as 02/04 was being filed as 4 February.
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale() ?? enGB}>
      <div className="inner">
        <DashboardPageHeader
          title={setup?.title ?? t('demographics.title')}
          subtitle={setup?.subtitle ?? t('demographics.subtitle')}
        />

        {loadFailed && (
          <DataLoadErrorAlert
            message={t('demographics.loadError')}
            onRetry={load}
          />
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {t(error)}
          </Alert>
        )}
        {successMsg && (
          <Alert
            severity="success"
            onClose={() => setSuccessMsg(null)}
            sx={{ mb: 2 }}
          >
            {t(successMsg)}
          </Alert>
        )}

        {!loadFailed && (
          <>
            {/* In setup the subtitle is the MasterChart intro, so the why line moves here. */}
            {setup && <Typography sx={{ mb: 3 }}>{t('demographics.subtitle')}</Typography>}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <DatePicker
                        value={field.value}
                        onChange={(date) => field.onChange(date)}
                        maxDate={new Date()}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            label: t('demographics.dateOfBirth'),
                            error: !!fieldError,
                            helperText:
                              fieldError?.message ??
                              (age != null ? t('demographics.age', { age, count: age }) : ''),
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="sex"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="sex-label">{t('demographics.sex')}</InputLabel>
                        <Select
                          {...field}
                          labelId="sex-label"
                          label={t('demographics.sex')}
                        >
                          <MenuItem value="male">{t('demographics.sexOptions.male')}</MenuItem>
                          <MenuItem value="female">{t('demographics.sexOptions.female')}</MenuItem>
                          <MenuItem value="other">{t('demographics.sexOptions.other')}</MenuItem>
                          <MenuItem value="prefer-not-to-say">
                            {t('demographics.sexOptions.prefer-not-to-say')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="weight"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('demographics.weight')}
                        type="number"
                        fullWidth
                        inputProps={{ min: 0, step: 0.1 }}
                        helperText={t('demographics.weightHelp')}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* In setup, Continue saves instead. */}
            {setup ? (
              setup.footer
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <LoadingButton
                  variant="contained"
                  onClick={handleSave}
                  loading={saving}
                  disabled={saving}
                >
                  {t('common.save')}
                </LoadingButton>
                <MasterChartBackButton />
              </Box>
            )}
          </>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default DemographicsPage;
